import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import {
  IVacationAwardingPublishRequest,
  IVacationAwardingPublishResponse,
  IVacationAwardingResponse,
  IVacationAwardingSavePayload,
  IVacationAwardingSaveResponse,
  IVacationBidCrew,
  IVacationBidCrewAddRequest,
  IVacationBidCrewEntitlementPreview,
  IVacationBidCrewEntitlementPreviewRequest,
  IVacationBidCrewImportPreviewResponse,
  IVacationBidCrewImportRequest,
  IVacationBidCrewImportResponse,
  IVacationAwardImportPreviewResponse,
  IVacationAwardImportRequest,
  IVacationAwardImportResult,
  IVacationAwardImportRow,
  IVacationBidCrewImportRow,
  IVacationBidCrewImportRowPreview,
  IVacationBidCrewListItem,
  IVacationBidCrewUpdateRequest,
  IVacationBidCrewUpdateResponse,
  IVacationBidPackage,
  IVacationBidPackageListItem,
  IVacationBidPackageResponse,
  IVacationBidSlot,
  IVacationBidSlotGeneration,
  IVacationBidSlotGenerationRequest,
  IVacationBidSlotGenerationResponse,
  IVacationChangeRequestDecision,
  IVacationChangeRequestDecisionResponse,
  IVacationChangeRequestsResponse,
  IVacationChangeWindowPayload,
  IVacationDate,
  IVacationNfaBuildStatusResponse,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";
import moment from "moment";

const BASE_PATH = "/vacationBidPackages";

// ---------------------------------------------------------------------------
// API
// Every call returns null when the request fails; callers must check the shape.
// ---------------------------------------------------------------------------

export const getVacationBidPackages = async (): Promise<IVacationBidPackageListItem[]> => {
  const response = await AmplifyAPIWrapper.Get<IVacationBidPackageListItem[]>(BASE_PATH, null);
  return Array.isArray(response) ? response : [];
};

export const getVacationBidPackage = async (
  packageId: string,
  notifications: INotifications | null = null
): Promise<IVacationBidPackageResponse | null> => {
  return AmplifyAPIWrapper.Get<IVacationBidPackageResponse>(`${BASE_PATH}/${packageId}`, notifications);
};

export const createVacationBidPackage = async (
  payload: Partial<IVacationBidPackage>,
  notifications: INotifications
): Promise<IVacationBidPackageResponse | null> => {
  return AmplifyAPIWrapper.Post<IVacationBidPackageResponse>(BASE_PATH, payload, notifications);
};

/**
 * Updates a package's details. Crew groups save like any other field - the crew list is only built
 * from them when an admin asks for it through {@link buildVacationBidCrew}.
 */
export const updateVacationBidPackage = async (
  packageId: string,
  payload: Partial<IVacationBidPackage>,
  notifications: INotifications
): Promise<IVacationBidPackageResponse | null> => {
  return AmplifyAPIWrapper.Put<IVacationBidPackageResponse>(
    `${BASE_PATH}/${packageId}`,
    payload as Partial<IVacationBidPackageResponse>,
    notifications
  );
};

/**
 * Saves the whole slot table. The backend merges it against the stored slots and refuses to drop
 * or re-date a slot once the package has opened to crew.
 */
export const saveVacationBidSlots = async (
  packageId: string,
  slots: IVacationBidSlot[],
  notifications: INotifications
): Promise<IVacationBidPackageResponse | null> => {
  return AmplifyAPIWrapper.Put<any>(`${BASE_PATH}/${packageId}/slots`, slots, notifications);
};

/**
 * Builds candidate slots from generation parameters. Nothing is saved - the result is for the
 * admin to review and then submit through {@link saveVacationBidSlots}.
 */
export const generateVacationBidSlots = async (
  packageId: string,
  generation: IVacationBidSlotGeneration,
  notifications: INotifications
): Promise<IVacationBidSlot[] | null> => {
  const body: IVacationBidSlotGenerationRequest = { generation };
  const response = await AmplifyAPIWrapper.Post<IVacationBidSlotGenerationResponse>(
    `${BASE_PATH}/${packageId}/slots/generate`,
    body,
    notifications
  );
  return Array.isArray(response?.slots) ? response.slots : null;
};

export const getVacationBidCrew = async (packageId: string): Promise<IVacationBidCrewListItem[]> => {
  const response = await AmplifyAPIWrapper.Get<IVacationBidCrewListItem[]>(`${BASE_PATH}/${packageId}/crew`, null);
  return Array.isArray(response) ? response : [];
};

/** Saves a crew member's days, with the changes to their awards the body asks for, and says what it did to them. */
export const updateVacationBidCrew = async (
  packageId: string,
  userId: string,
  body: IVacationBidCrewUpdateRequest,
  notifications: INotifications
): Promise<IVacationBidCrewUpdateResponse | null> => {
  return AmplifyAPIWrapper.Put<IVacationBidCrewUpdateResponse>(
    `${BASE_PATH}/${packageId}/crew/${userId}`,
    // The wrapper types a request body against its response type, so the request shape is cast.
    body as unknown as Partial<IVacationBidCrewUpdateResponse>,
    notifications
  );
};

/** What editing a crew member's days would change in their awards. Saves nothing. */
export const previewVacationBidCrewEntitlement = async (
  packageId: string,
  userId: string,
  body: IVacationBidCrewEntitlementPreviewRequest,
  notifications: INotifications
): Promise<IVacationBidCrewEntitlementPreview | null> => {
  const response = await AmplifyAPIWrapper.Post<IVacationBidCrewEntitlementPreview>(
    `${BASE_PATH}/${packageId}/crew/${userId}/preview`,
    body as unknown as Partial<IVacationBidCrewEntitlementPreview>,
    notifications
  );
  return Array.isArray(response?.adjustments) ? response : null;
};

/** Takes a warning about an entitlement change off a crew member. */
export const dismissVacationAwardWarning = async (
  packageId: string,
  userId: string,
  warningId: string,
  notifications: INotifications
): Promise<boolean> => {
  const response = await AmplifyAPIWrapper.Del<{ dismissed: boolean }>(
    `${BASE_PATH}/${packageId}/crew/${userId}/warnings/${warningId}`,
    undefined,
    notifications
  );
  return !!response?.dismissed;
};

/** Adds one crew member to a package. Entitlements start at zero unless given. */
export const addVacationBidCrew = async (
  packageId: string,
  userId: string,
  body: IVacationBidCrewAddRequest,
  notifications: INotifications
): Promise<IVacationBidCrew | null> => {
  return AmplifyAPIWrapper.Post<IVacationBidCrew>(`${BASE_PATH}/${packageId}/crew/${userId}`, body, notifications);
};

/** Takes one crew member out of a package, along with the entitlements held for them in it. */
export const removeVacationBidCrew = async (
  packageId: string,
  userId: string,
  notifications: INotifications
): Promise<{ removed: number } | null> => {
  return AmplifyAPIWrapper.Del<{ removed: number }>(
    `${BASE_PATH}/${packageId}/crew/${userId}`,
    undefined,
    notifications
  );
};

/**
 * Builds the crew list from the package's crew groups, replacing what is there. Crew added from a
 * file or by hand who are not in the groups are removed, so this is always confirmed first.
 */
export const buildVacationBidCrew = async (
  packageId: string,
  notifications: INotifications
): Promise<{ crewCount: number } | null> => {
  return AmplifyAPIWrapper.Post<{ crewCount: number }>(`${BASE_PATH}/${packageId}/crew/build`, {}, notifications);
};

/**
 * Reports what an entitlement file would do. Persists nothing - employee ids are resolved against
 * the crew list and the package so the admin sees the outcome before confirming.
 */
export const previewVacationBidCrewImport = async (
  packageId: string,
  rows: IVacationBidCrewImportRow[],
  notifications: INotifications
): Promise<IVacationBidCrewImportRowPreview[] | null> => {
  const body: IVacationBidCrewImportRequest = { rows };
  const response = await AmplifyAPIWrapper.Post<IVacationBidCrewImportPreviewResponse>(
    `${BASE_PATH}/${packageId}/crew/import`,
    body,
    notifications
  );
  return Array.isArray(response?.rows) ? response.rows : null;
};

/** Applies an entitlement file, adding crew it names who are not yet in the package. */
export const importVacationBidCrew = async (
  packageId: string,
  rows: IVacationBidCrewImportRow[],
  notifications: INotifications
): Promise<IVacationBidCrewImportResponse | null> => {
  const body: IVacationBidCrewImportRequest = { rows };
  return AmplifyAPIWrapper.Put<IVacationBidCrewImportResponse>(
    `${BASE_PATH}/${packageId}/crew/import`,
    // The wrapper types a request body against its response type, so the request shape is cast.
    body as unknown as Partial<IVacationBidCrewImportResponse>,
    notifications
  );
};

/**
 * Reports what an award file would do, without writing anything.
 *
 * The file is read in the browser, so this only ever sends rows that have already been parsed.
 * What an employee number names, whether a week matches a block, and whether the days add up are
 * all decided by the server.
 */
export const previewVacationAwardImport = async (
  packageId: string,
  rows: IVacationAwardImportRow[],
  skipEntitlementChecks: boolean,
  notifications: INotifications
): Promise<IVacationAwardImportPreviewResponse | null> => {
  const body: IVacationAwardImportRequest = { rows, skipEntitlementChecks };
  const response = await AmplifyAPIWrapper.Post<IVacationAwardImportPreviewResponse>(
    `${BASE_PATH}/${packageId}/awarding/import`,
    body,
    notifications
  );
  // The wrapper hands back an error body as readily as a preview, so the shape is what decides
  // whether there is one. The screen reads these three without checking, as `getVacationAwarding`
  // below lets it.
  return Array.isArray(response?.rows) && Array.isArray(response?.held) && Array.isArray(response?.omitted)
    ? response
    : null;
};

/** Applies an award file, replacing what the crew it names hold in the rounds it can write. */
export const importVacationAwards = async (
  packageId: string,
  rows: IVacationAwardImportRow[],
  skipEntitlementChecks: boolean,
  notifications: INotifications
): Promise<IVacationAwardImportResult | null> => {
  const body: IVacationAwardImportRequest = { rows, skipEntitlementChecks };
  const response = await AmplifyAPIWrapper.Put<IVacationAwardImportResult>(
    `${BASE_PATH}/${packageId}/awarding/import`,
    // The wrapper types a request body against its response type, so the request shape is cast.
    body as unknown as Partial<IVacationAwardImportResult>,
    notifications
  );
  // The wrapper shows its success message for an error body too, and returns the body either way,
  // so an error would otherwise be reported as an import of `undefined` crew members.
  return typeof response?.applied === "number" && Array.isArray(response.skipped) ? response : null;
};

/** Everything the View Bids and Award Bids tabs show for one round. */
export const getVacationAwarding = async (
  packageId: string,
  round: VacationBidRound,
  notifications: INotifications | null = null
): Promise<IVacationAwardingResponse | null> => {
  const response = await AmplifyAPIWrapper.Get<IVacationAwardingResponse>(
    `${BASE_PATH}/${packageId}/awarding?round=${round}`,
    notifications
  );
  return Array.isArray(response?.crew) && Array.isArray(response?.usage) ? response : null;
};

/**
 * Saves awarding changes - awards added or taken away, and crew progress. Carries only what
 * changed; the response holds the crew it touched and fresh usage for every slot.
 */
export const saveVacationAwarding = async (
  packageId: string,
  payload: IVacationAwardingSavePayload,
  notifications: INotifications
): Promise<IVacationAwardingSaveResponse | null> => {
  const response = await AmplifyAPIWrapper.Put<IVacationAwardingSaveResponse>(
    `${BASE_PATH}/${packageId}/awarding`,
    // The wrapper types a request body against its response type, so the request shape is cast.
    payload as unknown as Partial<IVacationAwardingSaveResponse>,
    notifications
  );
  return Array.isArray(response?.usage) && Array.isArray(response?.crew) ? response : null;
};

/** Releases a round's awards to crew. The round is read-only afterwards. */
export const publishVacationAwarding = async (
  packageId: string,
  round: VacationBidRound,
  notifications: INotifications
): Promise<IVacationAwardingPublishResponse | null> => {
  const body: IVacationAwardingPublishRequest = { round };
  const response = await AmplifyAPIWrapper.Post<IVacationAwardingPublishResponse>(
    `${BASE_PATH}/${packageId}/awarding/publish`,
    body,
    notifications
  );
  return response?.publication ? response : null;
};

/**
 * The package's latest NFA build, whether another can start (and why not), and every crew
 * member's last outcome. Polled while a build runs, so it carries no notifications by default.
 */
export const getVacationNfaBuildStatus = async (
  packageId: string,
  notifications: INotifications | null = null
): Promise<IVacationNfaBuildStatusResponse | null> => {
  const response = await AmplifyAPIWrapper.Get<IVacationNfaBuildStatusResponse>(
    `${BASE_PATH}/${packageId}/nfaBuild`,
    notifications
  );
  return Array.isArray(response?.crew) ? response : null;
};

/**
 * Starts building the package's NFAs from its published awards. Returns as soon as the build is
 * recorded; it runs in the background, and the status shows how it is going.
 */
export const startVacationNfaBuild = async (
  packageId: string,
  notifications: INotifications
): Promise<IVacationNfaBuildStatusResponse | null> => {
  const response = await AmplifyAPIWrapper.Post<IVacationNfaBuildStatusResponse>(
    `${BASE_PATH}/${packageId}/nfaBuild`,
    {},
    notifications
  );
  return Array.isArray(response?.crew) && response?.build ? response : null;
};

// ---------------------------------------------------------------------------
// Change requests
// Every window write returns the whole Manage Awards view; a decision returns only what it touched.
// ---------------------------------------------------------------------------

const isChangeView = (response: IVacationChangeRequestsResponse | null): boolean =>
  Array.isArray(response?.windows) && Array.isArray(response?.requests) && Array.isArray(response?.usage);

/** Loads the whole Manage Awards view for a package. */
export const getVacationChangeRequests = async (
  packageId: string,
  notifications: INotifications | null = null
): Promise<IVacationChangeRequestsResponse | null> => {
  const response = await AmplifyAPIWrapper.Get<IVacationChangeRequestsResponse>(
    `${BASE_PATH}/${packageId}/changeRequests`,
    notifications
  );
  return isChangeView(response) ? response : null;
};

export const createVacationChangeWindow = async (
  packageId: string,
  payload: IVacationChangeWindowPayload,
  notifications: INotifications
): Promise<IVacationChangeRequestsResponse | null> => {
  const response = await AmplifyAPIWrapper.Post<IVacationChangeRequestsResponse>(
    `${BASE_PATH}/${packageId}/changeWindows`,
    payload,
    notifications
  );
  return isChangeView(response) ? response : null;
};

export const updateVacationChangeWindow = async (
  packageId: string,
  windowId: string,
  payload: IVacationChangeWindowPayload,
  notifications: INotifications
): Promise<IVacationChangeRequestsResponse | null> => {
  const response = await AmplifyAPIWrapper.Put<IVacationChangeRequestsResponse>(
    `${BASE_PATH}/${packageId}/changeWindows/${windowId}`,
    // The wrapper types a request body against its response type, so the request shape is cast.
    payload as unknown as Partial<IVacationChangeRequestsResponse>,
    notifications
  );
  return isChangeView(response) ? response : null;
};

export const deleteVacationChangeWindow = async (
  packageId: string,
  windowId: string,
  notifications: INotifications
): Promise<IVacationChangeRequestsResponse | null> => {
  const response = await AmplifyAPIWrapper.Del<IVacationChangeRequestsResponse>(
    `${BASE_PATH}/${packageId}/changeWindows/${windowId}`,
    undefined,
    notifications
  );
  return isChangeView(response) ? response : null;
};

/** Shares a draft window to crew. */
export const shareVacationChangeWindow = async (
  packageId: string,
  windowId: string,
  notifications: INotifications
): Promise<IVacationChangeRequestsResponse | null> => {
  const response = await AmplifyAPIWrapper.Post<IVacationChangeRequestsResponse>(
    `${BASE_PATH}/${packageId}/changeWindows/${windowId}/share`,
    {},
    notifications
  );
  return isChangeView(response) ? response : null;
};

/** Approves or denies a change request once its window has closed. */
export const decideVacationChangeRequest = async (
  packageId: string,
  requestId: string,
  decision: IVacationChangeRequestDecision,
  notifications: INotifications
): Promise<IVacationChangeRequestDecisionResponse | null> => {
  const response = await AmplifyAPIWrapper.Post<IVacationChangeRequestDecisionResponse>(
    `${BASE_PATH}/${packageId}/changeRequests/${requestId}/decision`,
    decision,
    notifications
  );
  return response?.request && Array.isArray(response?.usage) && Array.isArray(response?.awards) ? response : null;
};

// ---------------------------------------------------------------------------
// Dates
// Slot boundaries are whole days with no timezone, so they never pass through Date.
// ---------------------------------------------------------------------------

/** `IVacationDate` to `YYYY-MM-DD`, the form the date picker and the grid work in. */
export const vacationDateToIso = (date: IVacationDate | null | undefined): string => {
  if (!date) return "";
  const mm = String(date.month).padStart(2, "0");
  const dd = String(date.day).padStart(2, "0");
  return `${date.year}-${mm}-${dd}`;
};

/** `YYYY-MM-DD` (or any ISO date-time, of which only the calendar date is used) to `IVacationDate`. */
export const isoToVacationDate = (value: string | null | undefined): IVacationDate | null => {
  if (!value) return null;
  const parsed = moment.utc(value.slice(0, 10), "YYYY-MM-DD", true);
  if (!parsed.isValid()) return null;
  return { year: parsed.year(), month: parsed.month() + 1, day: parsed.date() };
};

/** A `Date` or ISO string to the calendar date it falls on in UTC. */
export const dateToVacationDate = (value: Date | string | null | undefined): IVacationDate | null => {
  if (!value) return null;
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return null;
  return { year: parsed.year(), month: parsed.month() + 1, day: parsed.date() };
};

/** Display form, for example `Tue Jan 05, 2027`. */
export const formatVacationDate = (date: IVacationDate | null | undefined): string => {
  if (!date) return "";
  return moment.utc({ year: date.year, month: date.month - 1, day: date.day }).format("ddd MMM DD, YYYY");
};

export const compareVacationDates = (a: IVacationDate, b: IVacationDate): number =>
  a.year - b.year || a.month - b.month || a.day - b.day;

/** Inclusive day count between two calendar dates. */
export const vacationDateSpanDays = (start: IVacationDate, end: IVacationDate): number =>
  moment
    .utc({ year: end.year, month: end.month - 1, day: end.day })
    .diff(moment.utc({ year: start.year, month: start.month - 1, day: start.day }), "days") + 1;

// ---------------------------------------------------------------------------
// Slot ids
// ---------------------------------------------------------------------------

/**
 * A slot id is `YYYY-MMM-DD`. The stored id always carries the year; the customer's
 * `showSlotIdYear` client setting decides whether the UI shows it.
 */
export const displaySlotId = (slotId: string | undefined, showYear: boolean): string => {
  if (!slotId) return "";
  return showYear ? slotId : slotId.replace(/^\d{4}-/, "");
};

/** Mirrors the backend's id derivation so a freshly added row can show its id before it is saved. */
export const buildSlotId = (startDate: IVacationDate): string =>
  moment
    .utc({ year: startDate.year, month: startDate.month - 1, day: startDate.day })
    .format("YYYY-MMM-DD")
    .toUpperCase();

// ---------------------------------------------------------------------------
// Package shape helpers
// ---------------------------------------------------------------------------

export const hasSecondRound = (pkg: Partial<IVacationBidPackage> | null | undefined): boolean =>
  !!pkg?.secondBidWindowOpen && !!pkg?.secondBidWindowClose;

/**
 * Slots are protected from the moment crew can see them: none may be removed or re-dated after
 * the first bid window opens.
 */
export const slotsAreProtected = (pkg: Partial<IVacationBidPackage> | null | undefined, now = new Date()): boolean =>
  !!pkg?.bidWindowOpen && now >= new Date(pkg.bidWindowOpen);

/** setTimeout counts milliseconds in a signed 32-bit int, and overflows to firing at once. */
export const MAX_TIMER_MS = 2147483647;

const ROUND_1_FIELDS = ["bidWindowOpen", "bidWindowClose", "maxAwardsFirstRound"];
const ROUND_FIELDS = [...ROUND_1_FIELDS, "secondBidWindowOpen", "secondBidWindowClose"];

/**
 * The bid window fields that can no longer change: round 1's once it is published, and all of them once every round
 * is, as the server rules. `alsoPublished` are rounds published in this editor that the loaded package lacks.
 */
export const lockedRoundFields = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  alsoPublished: VacationBidRound[] = []
): Set<string> => {
  const published = new Set([...(pkg?.awardPublications ?? []).map((z) => z.round), ...alsoPublished]);
  if (packageRounds(pkg).every((round) => published.has(round))) return new Set(ROUND_FIELDS);
  return new Set(published.has(VacationBidRound.Round1) ? ROUND_1_FIELDS : []);
};

/** The rounds a package runs, in order. */
export const packageRounds = (pkg: Partial<IVacationBidPackage> | null | undefined): VacationBidRound[] =>
  hasSecondRound(pkg) ? [VacationBidRound.Round1, VacationBidRound.Round2] : [VacationBidRound.Round1];

/** The last round the package runs. */
export const lastRound = (pkg: Partial<IVacationBidPackage> | null | undefined): VacationBidRound =>
  hasSecondRound(pkg) ? VacationBidRound.Round2 : VacationBidRound.Round1;

/** Whether a round's bid window has closed, which is when it can be awarded. */
export const roundBiddingHasClosed = (
  pkg: Partial<IVacationBidPackage> | null | undefined,
  round: VacationBidRound,
  now = new Date()
): boolean => {
  const close = round === VacationBidRound.Round1 ? pkg?.bidWindowClose : pkg?.secondBidWindowClose;
  return !!close && now >= new Date(close);
};

/**
 * Names an exported file after its package, so several exports stay apart in a downloads folder.
 * Characters a filesystem would refuse are dropped rather than replaced.
 */
export const vacationCsvFileName = (packageTitle: string | undefined, suffix: string): string => {
  const cleaned = (packageTitle ?? "").replace(/[\\/:*?"<>|]/g, "").trim();
  return cleaned ? `${cleaned} - ${suffix}.csv` : `vacation-${suffix}.csv`;
};

export const emptyVacationBidPackage = (): IVacationBidPackage => ({
  _id: undefined,
  isDisabled: false,
  title: undefined,
  baseAirport: undefined,
  bidWindowOpen: undefined,
  bidWindowClose: undefined,
  secondBidWindowOpen: null,
  secondBidWindowClose: null,
  bidBlockStart: undefined,
  bidBlockEnd: undefined,
  maxAwardsFirstRound: null,
  crewGroups: [],
  slots: [],
  createdAt: undefined,
  createdBy: undefined,
  modifiedAt: undefined,
  modifiedBy: undefined,
});

export const defaultSlotGeneration = (bidBlockStart: Date | string | null | undefined): IVacationBidSlotGeneration => ({
  firstSlotStart: dateToVacationDate(bidBlockStart) ?? { year: new Date().getUTCFullYear(), month: 1, day: 1 },
  slotLengthDays: 5,
  repeatEveryDays: 7,
  defaultCapacity: 24,
  blackoutDates: [],
});
